import React, { useRef, useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Stack,
  Grow,
  Paper,
  Rating,
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AssessmentIcon from '@mui/icons-material/Assessment';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useTranslation } from 'react-i18next';
import { config } from '../../../config';
import {
  MediaRecorderState,
  ProgressModal,
  FeedbackSnackbar,
  uploadToScreenApp,
  stopMediaRecording,
} from './common';

interface VoiceAnalysis {
  rating: number;
  review: string;
}

export const VoiceTester: React.FC = () => {
  const { t } = useTranslation();
  const [state, setState] = useState<MediaRecorderState>({
    isRecording: false,
    uploading: false,
    progress: 0,
    uploadComplete: false,
    error: null,
  });
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState('00:00');
  const [analysis, setAnalysis] = useState<VoiceAnalysis | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const audioContext = useRef<AudioContext | null>(null);
  const analyser = useRef<AnalyserNode | null>(null);
  const visualizerRef = useRef<HTMLCanvasElement>(null);
  const animationFrame = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0);
  const timerInterval = useRef<ReturnType<typeof setInterval> | null>(null);
  const pausedTimeRef = useRef<number>(0);
  const recordedBlob = useRef<Blob | null>(null);
  const audioStream = useRef<MediaStream | null>(null);

  const updateRecordingTime = () => {
    const elapsedTime = Math.floor((Date.now() - startTimeRef.current) / 1000);
    const minutes = Math.floor(elapsedTime / 60)
      .toString()
      .padStart(2, '0');
    const seconds = (elapsedTime % 60).toString().padStart(2, '0');
    setRecordingTime(`${minutes}:${seconds}`);
  };

  const drawVisualizer = () => {
    if (!visualizerRef.current || !analyser.current) return;

    const canvasCtx = visualizerRef.current.getContext('2d');
    if (!canvasCtx) return;

    const { width, height } = visualizerRef.current;
    const dataArray = new Uint8Array(analyser.current.frequencyBinCount);

    const draw = () => {
      if (!analyser.current) return;

      analyser.current.getByteFrequencyData(dataArray);
      canvasCtx.clearRect(0, 0, width, height);

      const barWidth = Math.ceil(
        (width / analyser.current.frequencyBinCount) * 2.5
      );
      const centerY = height / 2;
      let x = 0;

      canvasCtx.fillStyle = '#d1d5db';
      for (let i = 0; i < analyser.current.frequencyBinCount; i += 1) {
        const barHeight = Math.min(dataArray[i] * 1.5, height);
        canvasCtx.fillRect(
          Math.floor(x),
          Math.floor(centerY - barHeight / 2),
          Math.floor(barWidth),
          Math.floor(barHeight)
        );
        x += barWidth + 1;
      }

      animationFrame.current = requestAnimationFrame(draw);
    };

    draw();
  };

  const analyzeVoice = async (audioBlob: Blob) => {
    try {
      // Convert audio blob to base64
      const base64Audio = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64 = (reader.result as string).split(',')[1];
          resolve(base64);
        };
        reader.readAsDataURL(audioBlob);
      });

      // Send to Gemini API for analysis
      const response = await fetch(config.geminiProxyUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'gemini-1.5-flash-8b',
          contents: [
            {
              parts: [
                {
                  text:
                    'You are ScreenApp\'s professional voice analysis AI. Evaluate this voice recording comprehensively and provide: 1) A rating from 1-10, and 2) A detailed analysis with specific quotes or examples from the audio. Focus on key aspects: voice clarity, tone quality, pitch accuracy, and overall audio quality. If singing, evaluate musical ability and song interpretation with specific musical moments. If speaking, assess articulation, pace, and engagement by quoting exact phrases and explaining their delivery. Consider microphone quality and audio settings. Format response exactly like this: \'8/10 - Clear articulation demonstrated in "[exact quote]". Natural tone with good pitch control, especially during "[specific moment]". Audio settings well-optimized as evidenced by [specific technical observation].\' Keep feedback constructive and actionable, always referencing actual content from the recording.',
                },
                {
                  inlineData: {
                    mimeType: 'audio/webm',
                    data: base64Audio,
                  },
                },
              ],
            },
          ],
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to analyze voice');
      }

      const data = await response.json();
      const result = data.candidates[0].content.parts[0].text;

      // Parse the markdown response
      const cleanResult = result.replace(/[*_`]/g, '').trim(); // Remove markdown symbols
      const match = cleanResult.match(/(\d+)\/10\s*-\s*(.*)/);
      if (match) {
        setAnalysis({
          rating: parseInt(match[1], 10),
          review: match[2].trim(),
        });
      } else {
        throw new Error('Failed to parse voice analysis');
      }
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error: 'Failed to analyze voice. Please try again.',
      }));
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStream.current = stream;

      audioContext.current = new (window.AudioContext ||
        (window as any).webkitAudioContext)();
      analyser.current = audioContext.current.createAnalyser();
      const source = audioContext.current.createMediaStreamSource(stream);
      source.connect(analyser.current);

      analyser.current.fftSize = 2048;
      analyser.current.minDecibels = -90;
      analyser.current.maxDecibels = -10;
      analyser.current.smoothingTimeConstant = 0.85;

      const options = {
        mimeType: 'audio/webm;codecs=opus',
        audioBitsPerSecond: 128000,
      };

      if (MediaRecorder.isTypeSupported(options.mimeType)) {
        mediaRecorder.current = new MediaRecorder(stream, options);
      } else {
        mediaRecorder.current = new MediaRecorder(stream);
      }

      mediaRecorder.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.current.push(event.data);
        }
      };

      mediaRecorder.current.onstop = async () => {
        const audioBlob = new Blob(chunks.current, {
          type: mediaRecorder.current?.mimeType || 'audio/webm;codecs=opus',
        });

        recordedBlob.current = audioBlob;
        // Create URL for audio preview
        const url = URL.createObjectURL(audioBlob);
        setAudioUrl(url);
        setState((prev) => ({ ...prev, uploading: true }));

        // Analyze the voice recording
        await analyzeVoice(audioBlob);

        setState((prev) => ({
          ...prev,
          isRecording: false,
          uploading: false,
          uploadComplete: true,
        }));

        // Clean up
        if (timerInterval.current) clearInterval(timerInterval.current);
        if (visualizerRef.current) visualizerRef.current.style.display = 'none';
        chunks.current = [];
      };

      mediaRecorder.current.start(1000);
      setState((prev) => ({ ...prev, isRecording: true }));
      startTimeRef.current = Date.now();
      updateRecordingTime();
      timerInterval.current = setInterval(updateRecordingTime, 1000);

      if (visualizerRef.current) {
        visualizerRef.current.style.display = 'block';
        drawVisualizer();
      }
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error: 'Error accessing microphone. Please check permissions.',
      }));
    }
  };

  const stopRecording = () => {
    if (mediaRecorder.current && state.isRecording) {
      if (timerInterval.current) {
        clearInterval(timerInterval.current);
        timerInterval.current = null;
      }
      stopMediaRecording(mediaRecorder, [audioStream.current], animationFrame);
    }
  };

  const togglePause = () => {
    if (!mediaRecorder.current) return;

    if (isPaused) {
      mediaRecorder.current.resume();
      startTimeRef.current = Date.now() - pausedTimeRef.current;
      timerInterval.current = setInterval(updateRecordingTime, 1000);
      drawVisualizer();
    } else {
      mediaRecorder.current.pause();
      pausedTimeRef.current = Date.now() - startTimeRef.current;
      if (timerInterval.current) clearInterval(timerInterval.current);
      if (animationFrame.current) cancelAnimationFrame(animationFrame.current);
    }
    setIsPaused(!isPaused);
  };

  const handleReset = () => {
    setState({
      isRecording: false,
      uploading: false,
      progress: 0,
      uploadComplete: false,
      error: null,
    });
    setIsPaused(false);
    setRecordingTime('00:00');
    setAnalysis(null);
    setIsPlaying(false);

    // Clean up audio URL
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
    }

    recordedBlob.current = null;
    chunks.current = [];
    startTimeRef.current = 0;
    pausedTimeRef.current = 0;

    stopMediaRecording(mediaRecorder, [audioStream.current], animationFrame);
    audioStream.current = null;

    if (timerInterval.current) {
      clearInterval(timerInterval.current);
      timerInterval.current = null;
    }

    if (audioContext.current?.state !== 'closed') {
      audioContext.current?.close();
    }
    audioContext.current = null;
    analyser.current = null;

    if (visualizerRef.current) {
      const ctx = visualizerRef.current.getContext('2d');
      if (ctx) {
        ctx.clearRect(
          0,
          0,
          visualizerRef.current.width,
          visualizerRef.current.height
        );
      }
      visualizerRef.current.style.display = 'none';
    }
  };

  useEffect(() => {
    return () => {
      if (timerInterval.current) clearInterval(timerInterval.current);
      if (animationFrame.current) cancelAnimationFrame(animationFrame.current);
    };
  }, []);

  return (
    <Box sx={{ textAlign: 'center', p: 3, maxWidth: 800, mx: 'auto' }}>
      {state.error?.includes('microphone') && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            backgroundColor: 'error.lighter',
            border: '1px solid',
            borderColor: 'error.light',
            borderRadius: 2,
          }}
        >
          <Typography variant="body2" color="error">
            {t('voiceTester.messages.error')}
          </Typography>
        </Paper>
      )}

      <Stack
        direction="row"
        spacing={2}
        justifyContent="center"
        sx={{ mb: 4, minHeight: 56 }}
      >
        {!state.isRecording && !state.uploading && (
          <Button
            variant="contained"
            color="error"
            onClick={startRecording}
            startIcon={<MicIcon />}
            size="large"
            sx={{
              minWidth: 200,
              px: 4,
              borderRadius: 28,
              py: 2,
              fontSize: '1.1rem',
              '& .MuiSvgIcon-root': {
                color: 'white',
              },
              '&:hover': {
                backgroundColor: 'error.dark',
                '& .MuiSvgIcon-root': {
                  color: 'white',
                },
              },
            }}
          >
            {t('voiceTester.controls.recordNow')}
          </Button>
        )}
        {state.isRecording && (
          <>
            <Grow in={state.isRecording} timeout={300}>
              <Button
                variant="contained"
                color="error"
                onClick={stopRecording}
                startIcon={<AssessmentIcon />}
                size="large"
                sx={{
                  minWidth: 180,
                  px: 3,
                  borderRadius: 28,
                  py: 2,
                  fontSize: '1.1rem',
                  '& .MuiSvgIcon-root': {
                    color: 'white',
                  },
                  '&:hover': {
                    backgroundColor: 'error.dark',
                    '& .MuiSvgIcon-root': {
                      color: 'white',
                    },
                  },
                }}
              >
                {t('voiceTester.controls.stop')} ({recordingTime})
              </Button>
            </Grow>
            <Grow in={state.isRecording} timeout={300}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={togglePause}
                startIcon={isPaused ? <PlayArrowIcon /> : <PauseIcon />}
                size="large"
                sx={{
                  minWidth: 140,
                  px: 3,
                  borderRadius: 28,
                  py: 2,
                  fontSize: '1.1rem',
                }}
              >
                {isPaused
                  ? t('voiceTester.controls.play')
                  : t('voiceTester.controls.stop')}
              </Button>
            </Grow>
          </>
        )}
      </Stack>

      <Box
        component="canvas"
        ref={visualizerRef}
        sx={{
          width: '100%',
          height: 150,
          my: 3,
          display: 'none',
          borderRadius: 2,
          backgroundColor: 'background.paper',
        }}
      />

      {analysis && (
        <Grow in={!!analysis} timeout={500}>
          <Paper
            elevation={0}
            sx={{
              p: 4,
              mt: 4,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 3,
              maxWidth: 600,
              mx: 'auto',
              backgroundColor: 'background.paper',
            }}
          >
            <Stack spacing={3} alignItems="center">
              {audioUrl && (
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      if (audioRef.current) {
                        if (isPlaying) {
                          audioRef.current.pause();
                        } else {
                          audioRef.current.play();
                        }
                        setIsPlaying(!isPlaying);
                      }
                    }}
                    startIcon={isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                    sx={{
                      borderRadius: 28,
                      minWidth: 120,
                    }}
                  >
                    {isPlaying
                      ? t('voiceTester.controls.pause')
                      : t('voiceTester.controls.play')}
                  </Button>
                  <audio
                    ref={audioRef}
                    src={audioUrl}
                    onEnded={() => setIsPlaying(false)}
                    style={{ display: 'none' }}
                  >
                    <track
                      kind="captions"
                      src=""
                      label="Voice recording preview"
                      default
                    />
                  </audio>
                </Box>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body1" color="text.secondary">
                  {t('voiceTester.analysis.qualityScore')}:
                </Typography>
                <Rating
                  value={analysis.rating}
                  readOnly
                  max={10}
                  size="large"
                  sx={{ ml: 1 }}
                />
                <Typography variant="h6" sx={{ ml: 1, color: 'primary.main' }}>
                  {analysis.rating}/10
                </Typography>
              </Box>
              <Typography
                variant="body1"
                sx={{
                  fontStyle: 'italic',
                  color: 'text.primary',
                  textAlign: 'center',
                  lineHeight: 1.6,
                }}
              >
                {analysis.review}
              </Typography>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => {
                  if (recordedBlob.current) {
                    uploadToScreenApp(
                      recordedBlob.current,
                      'voice-recording.webm',
                      () => setState((prev) => ({ ...prev, uploading: true })),
                      (progress) => setState((prev) => ({ ...prev, progress })),
                      () =>
                        setState((prev) => ({ ...prev, uploadComplete: true })),
                      (error) => setState((prev) => ({ ...prev, error }))
                    );
                  }
                }}
                startIcon={<RestartAltIcon />}
                sx={{
                  mt: 2,
                  borderRadius: 28,
                  px: 3,
                  py: 1,
                }}
              >
                {t('voiceTester.controls.tryAgain')}
              </Button>
            </Stack>
          </Paper>
        </Grow>
      )}

      <ProgressModal
        open={state.uploading}
        progress={state.progress}
        isComplete={state.uploadComplete}
        processingMessage={t('voiceTester.messages.loading')}
        completeMessage={t('voiceTester.messages.complete')}
        onReset={handleReset}
      />

      <FeedbackSnackbar
        open={!!state.error}
        message={state.error || t('voiceTester.messages.error')}
        severity="error"
        onClose={() => setState((prev) => ({ ...prev, error: null }))}
      />
    </Box>
  );
};

export default VoiceTester;
